<script setup>
/* global axios */
// Takvim bolumu: ay izgarasi + kayit paneli.
//
// Kullanicinin istegi "rahat, kolay, pratik" idi. Bu yuzden:
//   - acilista bu ayin izgarasi gelir,
//   - bos bir gune tiklayinca o tarihle yeni kayit paneli acilir,
//   - bir kayda tiklayinca ayni panel dolu acilir.
// Ayri bir "kayit ekle" SAYFASI yok: sayfa degistirmek acik ayi ve bakilan
// yeri kaybettiriyor.
//
// Ham 'axios' PAKETINI ice aktarmiyoruz. Chatwoot kimlik basliklarini
// window.axios uzerinde tutuyor; paketten gelen ornek 401 doner.
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import TakvimAy from './TakvimAy.vue';

const { t, locale } = useI18n();
const route = useRoute();
const router = useRouter();

const uc = computed(() => `/api/v1/accounts/${route.params.accountId}/takvim`);

// Kullanicinin secebilecegi turler. 'gonderi' BILEREK yok: o kayitlar ICERIK
// bolumundeki zamanlanmis gonderilerden turuyor, takvimden uretilemez.
const TURLER = ['hatirlatma', 'randevu', 'genel'];

// Anahtarlar acik yazilir: `TAKVIM.TUR_${tur}` gibi uretilen anahtarlar
// aramada bulunamiyor ve eksik ceviri fark edilmeden kaliyor.
const TUR_ANAHTARLARI = {
  hatirlatma: 'TAKVIM.TUR_HATIRLATMA',
  randevu: 'TAKVIM.TUR_RANDEVU',
  genel: 'TAKVIM.TUR_GENEL',
  gonderi: 'TAKVIM.TUR_GONDERI',
};

const DURUMLAR = ['acik', 'tamamlandi', 'iptal'];
const DURUM_ANAHTARLARI = {
  acik: 'TAKVIM.DURUM_ACIK',
  tamamlandi: 'TAKVIM.DURUM_TAMAMLANDI',
  iptal: 'TAKVIM.DURUM_IPTAL',
};

// Hatirlatma secenekleri dakika cinsinden; sunucu hatirlatma_dakika
// bekliyor. Bos dize "hatirlatma yok" demek, select bos degeri null'a
// cevrilerek gonderiliyor.
const HATIRLATMA_SECENEKLERI = [
  { deger: '', anahtar: 'TAKVIM.HATIRLATMA_YOK' },
  { deger: '10', anahtar: 'TAKVIM.HATIRLATMA_10DK' },
  { deger: '60', anahtar: 'TAKVIM.HATIRLATMA_1SAAT' },
  { deger: '1440', anahtar: 'TAKVIM.HATIRLATMA_1GUN' },
];

const RENK_SINIFLARI = {
  mavi: 'text-n-blue-11',
  mor: 'text-n-iris-11',
  yesil: 'text-n-teal-11',
  gri: 'text-n-slate-11',
};

// --- tarih yardimcilari ----------------------------------------------------

// Chatwoot dil kodlari yer yer alt cizgili geliyor (pt_BR gibi); Intl bunu
// gecersiz sayip hata firlatir, tireye cevirmek gerekiyor.
const bicimDili = computed(() => String(locale.value || 'tr').replace('_', '-'));

const iki = sayi => String(sayi).padStart(2, '0');

// Gun anahtari YEREL tarihten uretilir. toISOString() kullanirsak saat
// dilimi yuzunden kayit bir onceki/sonraki gune dusebiliyor.
const gunAnahtari = tarih =>
  `${tarih.getFullYear()}-${iki(tarih.getMonth() + 1)}-${iki(tarih.getDate())}`;

const saatAnahtari = tarih => `${iki(tarih.getHours())}:${iki(tarih.getMinutes())}`;

const gecerliTarih = (deger, yedek = null) => {
  const tarih = new Date(deger);
  return Number.isNaN(tarih.getTime()) ? yedek : tarih;
};

// Form alanlarindan ('2026-09-18' + '14:30') YEREL bir tarih uretir.
// Metni dogrudan new Date()'e vermiyoruz: saat dilimi yorumu tarayiciya
// gore degisebiliyor, parcalardan kurunca belirsizlik kalmiyor.
const yereldenTarih = (gun, saat) => {
  const [yil, ay, gunNo] = String(gun || '').split('-').map(Number);
  const [sa, dk] = String(saat || '00:00').split(':').map(Number);
  if (!yil || !ay || !gunNo) return null;
  const tarih = new Date(yil, ay - 1, gunNo, sa || 0, dk || 0, 0, 0);
  return Number.isNaN(tarih.getTime()) ? null : tarih;
};

// Tum gun kayitlarin bitisi gunun SON anidir. Ertesi gunun 00:00'ini
// yazarsak kayit izgarada bir gun fazla gorunur.
const gunSonu = gun => {
  const tarih = yereldenTarih(gun, '00:00');
  if (!tarih) return null;
  return new Date(
    tarih.getFullYear(),
    tarih.getMonth(),
    tarih.getDate(),
    23,
    59,
    59,
    0
  );
};

// Bitis tam gece yarisiysa kayit ertesi gune tasmamali; forma da bir gun
// fazla yazmamali. (TakvimAy izgarada ayni kurali uyguluyor.)
const bitisGunu = (bas, bit) => {
  if (!bit || bit.getTime() <= bas.getTime()) return bas;
  const geceYarisi =
    bit.getHours() === 0 &&
    bit.getMinutes() === 0 &&
    bit.getSeconds() === 0 &&
    bit.getMilliseconds() === 0;
  return geceYarisi ? new Date(bit.getTime() - 1) : bit;
};

const bicimle = (tarih, ayarlar) => {
  try {
    return tarih.toLocaleString(bicimDili.value, ayarlar);
  } catch (e) {
    return tarih.toLocaleString('tr-TR', ayarlar);
  }
};

// --- durum -----------------------------------------------------------------

const simdi = new Date();
const odakYil = ref(simdi.getFullYear());
const odakAy = ref(simdi.getMonth());
const gorunum = ref('ay');
// Baslik tiklaninca acilan ay/yil secici.
const seciciAcik = ref(false);
// Secicideki yil, odak yildan AYRI: kullanici yillar arasinda gezerken
// arkadaki takvim degismesin, ancak bir ay secince atlasin.
const seciciYil = ref(simdi.getFullYear());
// Yil gorunumunun verisi ayri tutuluyor; ay gorunumunun 42 gunluk
// penceresiyle karistirmamak icin.
const yilKayitlari = ref([]);
const yuklenenYil = ref(0);

const kayitlar = ref([]);
const yukleniyor = ref(false);
const hata = ref('');

// Panelde ya duzenlenebilir bir form ya da salt okunur bir gonderi kaydi
// durur; ikisi ayni anda olmaz.
const form = ref(null);
const okunanKayit = ref(null);
const formHatasi = ref('');
const kaydediliyor = ref(false);
const siliniyor = ref(false);

const panelAcik = computed(() => Boolean(form.value || okunanKayit.value));

const hataMetni = e =>
  e?.response?.data?.error || e?.message || t('TAKVIM.HATA_BILINMEYEN');

// Bos metni null gonderiyoruz: bos dize alani bosaltmaz, "" olarak saklanir.
const bosuNull = deger => {
  const metin = String(deger ?? '').trim();
  return metin === '' ? null : metin;
};

// --- veri ------------------------------------------------------------------

const ayAnahtari = computed(() => `${odakYil.value}-${iki(odakAy.value + 1)}`);

// Izgarada onceki/sonraki ayin gunleri de gorunuyor. Istegi o 42 gunun
// tamamini kapsayacak sekilde atiyoruz; yalnizca ayin 1'i ile son gunu
// arasini isteseydik izgaranin ilk ve son satiri hep bos gorunurdu.
const aralik = () => {
  const ayinIlki = new Date(odakYil.value, odakAy.value, 1);
  const kaydir = (ayinIlki.getDay() + 6) % 7;
  const bas = new Date(odakYil.value, odakAy.value, 1 - kaydir, 0, 0, 0, 0);
  const bit = new Date(
    bas.getFullYear(),
    bas.getMonth(),
    bas.getDate() + 41,
    23,
    59,
    59,
    999
  );
  return { bas: bas.toISOString(), bit: bit.toISOString() };
};

// Ay hizli degistirildiginde gec donen eski yanit yeni listenin uzerine
// yazmasin diye her istege sira numarasi veriyoruz.
let istekSayaci = 0;

// Ekranda hangi ayin verisi duruyor. Ayni ay icin ikinci kez istek
// atmamak icin bakiyoruz.
const yuklenenAy = ref('');

const kayitlariYukle = async (zorla = false) => {
  if (!zorla && yuklenenAy.value === ayAnahtari.value) return;

  const sira = (istekSayaci += 1);
  const istenenAy = ayAnahtari.value;
  yukleniyor.value = true;
  hata.value = '';
  const { bas, bit } = aralik();

  try {
    const { data } = await axios.get(uc.value, { params: { bas, bit } });
    if (sira !== istekSayaci) return;
    kayitlar.value = data.kayitlar || [];
    yuklenenAy.value = istenenAy;
  } catch (e) {
    if (sira !== istekSayaci) return;
    hata.value = hataMetni(e);
    // Basarisiz istek "bu ay yuklendi" sayilmamali; yoksa kullanici aya
    // geri dondugunde sessiz bos bir ekranla kalirdi.
    yuklenenAy.value = '';
  } finally {
    if (sira === istekSayaci) yukleniyor.value = false;
  }
};

// Yil gorunumu tum yili istiyor; ay gorunumunun 42 gunluk penceresi
// yetmez. Ayri bir istek ve ayri bir onbellek anahtari kullaniyoruz.
const yilYukle = async () => {
  if (yuklenenYil.value === odakYil.value) return;

  const istenen = odakYil.value;
  yukleniyor.value = true;
  hata.value = '';
  try {
    const { data } = await axios.get(uc.value, {
      params: {
        bas: new Date(istenen, 0, 1, 0, 0, 0, 0).toISOString(),
        bit: new Date(istenen, 11, 31, 23, 59, 59, 999).toISOString(),
      },
    });
    if (odakYil.value !== istenen) return;
    yilKayitlari.value = data.kayitlar || [];
    yuklenenYil.value = istenen;
  } catch (e) {
    hata.value = hataMetni(e);
    yuklenenYil.value = 0;
  } finally {
    yukleniyor.value = false;
  }
};

// Yil izgarasinda hangi gunlerde kayit var: 'YYYY-AA-GG' kumesi.
const yilinDoluGunleri = computed(() => {
  const kume = new Set();
  for (const k of yilKayitlari.value) {
    const t = new Date(k.baslangic);
    if (Number.isNaN(t.getTime())) continue;
    kume.add(`${t.getFullYear()}-${iki(t.getMonth() + 1)}-${iki(t.getDate())}`);
  }
  return kume;
});

// Bir ayin izgarasi: Pazartesi baslangicli, yalnizca o ayin gunleri
// (yil gorunumunde komsu ay gunlerini gostermek gurultu yapiyor).
const ayIzgarasi = ay => {
  const ilk = new Date(odakYil.value, ay, 1);
  const bosluk = (ilk.getDay() + 6) % 7;
  const gunSayisi = new Date(odakYil.value, ay + 1, 0).getDate();
  const hucreler = [];
  for (let i = 0; i < bosluk; i += 1) hucreler.push(null);
  for (let g = 1; g <= gunSayisi; g += 1) hucreler.push(g);
  return hucreler;
};

const yilGunAnahtari = (ay, gun) =>
  `${odakYil.value}-${iki(ay + 1)}-${iki(gun)}`;

const bugunMu = (ay, gun) => {
  const b = new Date();
  return (
    b.getFullYear() === odakYil.value && b.getMonth() === ay && b.getDate() === gun
  );
};

watch(gorunum, yeni => {
  seciciAcik.value = false;
  if (yeni === 'yil') yilYukle();
});

onMounted(() => {
  kayitlariYukle();
  window.addEventListener('keydown', klavye);
  window.addEventListener('keydown', kacisTusu);
  // 'click' degil 'mousedown': tiklama bittiginde panel zaten kapanmis
  // olsun, arada bir kare acik kalmasin.
  document.addEventListener('mousedown', disTiklama);
});

onUnmounted(() => {
  window.removeEventListener('keydown', klavye);
  window.removeEventListener('keydown', kacisTusu);
  document.removeEventListener('mousedown', disTiklama);
});

// --- ay gezinmesi ----------------------------------------------------------

const ayDegistir = adim => {
  // Gun 1 uzerinden ilerliyoruz: ayin 31'inden ay eksiltmek Date'te bir
  // sonraki aya tasiyor (31 Mart - 1 ay = 3 Mart).
  const yeni = new Date(odakYil.value, odakAy.value + adim, 1);
  odakYil.value = yeni.getFullYear();
  odakAy.value = yeni.getMonth();
  kayitlariYukle();
};

const yilDegistir = adim => {
  odakYil.value += adim;
  kayitlariYukle();
  if (gorunum.value === 'yil') yilYukle();
};

const buguneGit = () => {
  const bugun = new Date();
  odakYil.value = bugun.getFullYear();
  odakAy.value = bugun.getMonth();
  seciciAcik.value = false;
  kayitlariYukle();
  if (gorunum.value === 'yil') yilYukle();
};

// Secicideki bir aya tiklayinca oraya atla.
const ayaAtla = (yil, ay) => {
  odakYil.value = yil;
  odakAy.value = ay;
  seciciAcik.value = false;
  gorunum.value = 'ay';
  kayitlariYukle();
};

const seciciKutusu = ref(null);

const seciciyiAc = () => {
  seciciYil.value = odakYil.value;
  seciciAcik.value = !seciciAcik.value;
};

// Acik kalan bir panel ekranin geri kalanini orter ve kullanici onu
// kapatmanin yolunu arar. Disariya tiklamak ve Escape kapatsin.
const disTiklama = olay => {
  if (!seciciAcik.value) return;
  const kutu = seciciKutusu.value;
  if (kutu && !kutu.contains(olay.target)) seciciAcik.value = false;
};

const kacisTusu = olay => {
  if (olay.key === 'Escape') seciciAcik.value = false;
};

// Yil kutusuna elle yazmak: uzak bir yila gitmenin en kisa yolu.
const seciciYilYaz = olay => {
  const deger = Number(olay.target.value);
  if (!Number.isInteger(deger)) return;
  // Makul bir aralikta tut: yanlis yazimda takvimin 5 haneli bir yila
  // dusup bos ekran gostermesini istemiyoruz.
  if (deger < 1970 || deger > 2100) return;
  seciciYil.value = deger;
};

// Yil izgarasinin gun basliklari. TakvimAy ayni seyi kendi icinde uretiyor;
// burada tekrar etmek yerine ayni kalibi kullaniyoruz (1 Ocak 2024 Pazartesi).
const gunBasliklari = computed(() =>
  Array.from({ length: 7 }, (_, sira) =>
    bicimle(new Date(2024, 0, 1 + sira), { weekday: 'short' })
  )
);

const kisaAylar = computed(() =>
  Array.from({ length: 12 }, (_, i) =>
    bicimle(new Date(2020, i, 1), { month: 'short' })
  )
);

// Klavye: PageUp/PageDown ay, Shift ile yil atlar. Uzun listelerde fare
// ile ay ay tiklamak yerine tek tusla gezinmek cok daha hizli.
const klavye = olay => {
  if (olay.key !== 'PageUp' && olay.key !== 'PageDown') return;
  // Bir metin alanindayken sayfa tuslari takvimi kaydirmamali.
  const hedef = olay.target;
  if (hedef && ['INPUT', 'TEXTAREA', 'SELECT'].includes(hedef.tagName)) return;

  olay.preventDefault();
  const yon = olay.key === 'PageUp' ? -1 : 1;
  if (olay.shiftKey) yilDegistir(yon);
  else ayDegistir(yon);
};

const ayBasligi = computed(() =>
  bicimle(new Date(odakYil.value, odakAy.value, 1), {
    month: 'long',
    year: 'numeric',
  })
);

// Ust banttaki "Yeni kayit" hangi gune yazsin? Bakilan ay bu ayysa bugune,
// degilse ayin ilk gunune: kullanici gormedigi bir tarihe kayit yazmasin.
const varsayilanGun = () => {
  const bugun = new Date();
  if (
    bugun.getFullYear() === odakYil.value &&
    bugun.getMonth() === odakAy.value
  ) {
    return gunAnahtari(bugun);
  }
  return gunAnahtari(new Date(odakYil.value, odakAy.value, 1));
};

// --- liste gorunumu --------------------------------------------------------

// Liste kaydi YALNIZCA baslangic gununde gosterir. Ay izgarasindan farkli:
// cok gunlu bir kaydi her gununde tekrar yazmak listeyi okunmaz yapardi.
const listeGunleri = computed(() => {
  const kova = new Map();

  [...kayitlar.value]
    .sort(
      (a, b) =>
        new Date(a.baslangic).getTime() - new Date(b.baslangic).getTime()
    )
    .forEach(kayit => {
      const bas = gecerliTarih(kayit.baslangic);
      if (!bas) return;
      const anahtar = gunAnahtari(bas);
      if (!kova.has(anahtar)) {
        kova.set(anahtar, {
          anahtar,
          baslik: bicimle(bas, {
            weekday: 'long',
            day: 'numeric',
            month: 'long',
          }),
          bugunMu: anahtar === gunAnahtari(new Date()),
          kayitlar: [],
        });
      }
      kova.get(anahtar).kayitlar.push(kayit);
    });

  return [...kova.values()];
});

const saatMetni = kayit => {
  if (kayit.tum_gun) return t('TAKVIM.TUM_GUN_KISA');
  const bas = gecerliTarih(kayit.baslangic);
  if (!bas) return '';
  return bicimle(bas, { hour: '2-digit', minute: '2-digit' });
};

// Panelde kaydin ne zaman oldugunu tam yaziyoruz; salt okunur gonderi
// kaydinda tek bilgi kaynagi bu satir.
const zamanMetni = kayit => {
  const bas = gecerliTarih(kayit.baslangic);
  if (!bas) return '';
  if (kayit.tum_gun) {
    return `${bicimle(bas, { dateStyle: 'medium' })} - ${t('TAKVIM.TUM_GUN_KISA')}`;
  }
  return bicimle(bas, { dateStyle: 'medium', timeStyle: 'short' });
};

const renkSinifi = kayit => RENK_SINIFLARI[kayit.renk] || RENK_SINIFLARI.gri;

const durumSinifi = kayit =>
  kayit.durum === 'tamamlandi' || kayit.durum === 'iptal'
    ? 'line-through opacity-60'
    : '';

const turMetni = kayit =>
  t(TUR_ANAHTARLARI[kayit.tur] || 'TAKVIM.TUR_GENEL');

const basligiGoster = kayit => kayit.baslik || t('TAKVIM.BASLIKSIZ');

const kayitAnahtari = kayit => `${kayit.tur}-${kayit.id}`;

// --- panel -----------------------------------------------------------------

const bosForm = anahtar => ({
  id: null,
  baslik: '',
  aciklama: '',
  gun: anahtar,
  saat: '09:00',
  tum_gun: false,
  bitis_gun: anahtar,
  bitis_saat: '10:00',
  tur: 'hatirlatma',
  hatirlatma: '',
  durum: 'acik',
});

const paneliKapat = () => {
  form.value = null;
  okunanKayit.value = null;
  formHatasi.value = '';
};

const yeniKayit = anahtar => {
  okunanKayit.value = null;
  form.value = bosForm(anahtar || varsayilanGun());
  formHatasi.value = '';
};

const kaydiAc = kayit => {
  // tur='gonderi' kayitlar ICERIK bolumundeki zamanlanmis gonderiden
  // TUREMISTIR; takvim onlari yalnizca yansitir. Buradan degistirilebilir
  // yapmak iki ayri dogruluk kaynagi olustururdu.
  if (kayit.tur === 'gonderi' || kayit.duzenlenebilir === false) {
    form.value = null;
    formHatasi.value = '';
    okunanKayit.value = kayit;
    return;
  }

  const bas = gecerliTarih(kayit.baslangic, new Date());
  const bit = bitisGunu(bas, gecerliTarih(kayit.bitis));

  okunanKayit.value = null;
  formHatasi.value = '';
  form.value = {
    id: kayit.id,
    baslik: kayit.baslik || '',
    aciklama: kayit.aciklama || '',
    gun: gunAnahtari(bas),
    saat: saatAnahtari(bas),
    tum_gun: Boolean(kayit.tum_gun),
    bitis_gun: gunAnahtari(bit),
    bitis_saat: saatAnahtari(bit),
    // Sunucu tanimadigimiz bir tur gonderirse secim kutusu bos kalmasin.
    tur: TURLER.includes(kayit.tur) ? kayit.tur : 'genel',
    hatirlatma:
      kayit.hatirlatma_dakika === null || kayit.hatirlatma_dakika === undefined
        ? ''
        : String(kayit.hatirlatma_dakika),
    durum: DURUMLAR.includes(kayit.durum) ? kayit.durum : 'acik',
  };
};

// Izgarada hangi hucre vurgulansin.
const seciliGun = computed(() => {
  if (form.value) return form.value.gun;
  if (okunanKayit.value) {
    const bas = gecerliTarih(okunanKayit.value.baslangic);
    return bas ? gunAnahtari(bas) : '';
  }
  return '';
});

const duzenlemede = computed(() => Boolean(form.value?.id));

const kaydet = async () => {
  const f = form.value;
  if (!f) return;

  if (!f.baslik.trim()) {
    formHatasi.value = t('TAKVIM.BASLIK_ZORUNLU');
    return;
  }
  if (!f.gun) {
    formHatasi.value = t('TAKVIM.TARIH_ZORUNLU');
    return;
  }

  const bas = f.tum_gun
    ? yereldenTarih(f.gun, '00:00')
    : yereldenTarih(f.gun, f.saat);
  const bit = f.tum_gun
    ? gunSonu(f.bitis_gun || f.gun)
    : yereldenTarih(f.bitis_gun || f.gun, f.bitis_saat || f.saat);

  if (!bas || !bit) {
    formHatasi.value = t('TAKVIM.TARIH_GECERSIZ');
    return;
  }
  if (bit.getTime() < bas.getTime()) {
    formHatasi.value = t('TAKVIM.BITIS_ERKEN');
    return;
  }

  const govde = {
    kayit: {
      baslik: f.baslik.trim(),
      aciklama: bosuNull(f.aciklama),
      // Sunucuya ISO 8601 (UTC) gidiyor: kullanicinin saat dilimini
      // tasimayan ham metin gonderirsek kayit saatler kayabilir.
      baslangic: bas.toISOString(),
      bitis: bit.toISOString(),
      tum_gun: f.tum_gun,
      tur: f.tur,
      hatirlatma_dakika: f.hatirlatma === '' ? null : Number(f.hatirlatma),
      durum: f.durum,
    },
  };

  formHatasi.value = '';
  kaydediliyor.value = true;
  try {
    if (f.id) {
      await axios.patch(`${uc.value}/${f.id}`, govde);
    } else {
      await axios.post(uc.value, govde);
    }
    useAlert(t('TAKVIM.KAYDEDILDI'));
    paneliKapat();
    // Kaydin dustugu ay, bakilan aydan farkli olabilir; yine de acik ayi
    // tazeliyoruz, kullanici nereye baktiysa orasi dogru olsun.
    await kayitlariYukle(true);
  } catch (e) {
    formHatasi.value = hataMetni(e);
  } finally {
    kaydediliyor.value = false;
  }
};

const sil = async () => {
  const f = form.value;
  if (!f?.id) return;
  // Silme geri alinamaz; onay metninde bunu acikca yaziyoruz.
  if (!window.confirm(t('TAKVIM.SIL_ONAY'))) return;

  formHatasi.value = '';
  siliniyor.value = true;
  try {
    await axios.delete(`${uc.value}/${f.id}`);
    useAlert(t('TAKVIM.SILINDI'));
    paneliKapat();
    await kayitlariYukle(true);
  } catch (e) {
    formHatasi.value = hataMetni(e);
  } finally {
    siliniyor.value = false;
  }
};

// --- gonderi kaydi ---------------------------------------------------------

// Turemis kaydin kaynagi GONDERININ KENDISI: kaynak_id o gonderinin kimligi.
//
// Eskiden burada gelen kutusuna gidiliyordu; Icerik bolumu hesap hesap
// bolunmus oldugu icin baska bir adres yoktu. Artik BIR gonderi COK hesaba
// gidiyor, dolayisiyla "hangi kutu" sorusunun tek bir dogru cevabi yok.
// Gonderinin kendisini Icerikler listesinde aciyoruz: hedeflerin her birinin
// durumu ve hatasi orada yan yana duruyor.
const gonderiKimligi = computed(() => {
  const kayit = okunanKayit.value;
  if (!kayit || kayit.kaynak_tur !== 'OmnupGonderi') return null;
  const sayi = Number(kayit.kaynak_id);
  return Number.isNaN(sayi) || !sayi ? null : sayi;
});

const gonderiyeGit = () => {
  if (!gonderiKimligi.value) return;
  router.push({
    name: 'icerik_liste',
    params: { accountId: route.params.accountId },
    query: { gonderi: gonderiKimligi.value },
  });
};
</script>

<template>
  <div class="flex flex-col w-full h-full overflow-hidden bg-n-surface-1">
    <header class="flex flex-col gap-3 px-6 py-4 border-b border-n-weak">
      <div class="flex flex-wrap items-center gap-3">
        <div class="min-w-0">
          <h1 class="text-lg font-medium text-n-slate-12">
            {{ t('TAKVIM.BASLIK') }}
          </h1>
          <p class="text-xs text-n-slate-11">{{ t('TAKVIM.ALT_BASLIK') }}</p>
        </div>

        <span class="grow" />

        <Button
          size="sm"
          variant="faded"
          icon="i-lucide-refresh-cw"
          :label="t('TAKVIM.YENILE')"
          :disabled="yukleniyor"
          @click="kayitlariYukle(true)"
        />
        <Button
          size="sm"
          variant="solid"
          icon="i-lucide-plus"
          :label="t('TAKVIM.YENI_KAYIT')"
          @click="yeniKayit('')"
        />
      </div>

      <div class="flex flex-wrap items-center gap-2">
        <!-- Yil dugmeleri: bir yil oncesine gitmek 12 tiklama olmasin. -->
        <Button
          size="sm"
          variant="faded"
          icon="i-lucide-chevrons-left"
          :aria-label="t('TAKVIM.ONCEKI_YIL')"
          @click="yilDegistir(-1)"
        />
        <Button
          size="sm"
          variant="faded"
          icon="i-lucide-chevron-left"
          :aria-label="t('TAKVIM.ONCEKI_AY')"
          @click="ayDegistir(-1)"
        />

        <!-- Baslik bir dugme: tiklayinca ay/yil secici aciliyor. Uzak bir
             tarihe gitmenin en kisa yolu bu. -->
        <div ref="seciciKutusu" class="relative">
          <button
            type="button"
            class="inline-flex items-center gap-1 px-2 py-1 rounded-md min-w-[9.5rem] justify-center text-sm font-medium capitalize text-n-slate-12 hover:bg-n-alpha-1"
            :aria-expanded="seciciAcik"
            @click="seciciyiAc"
          >
            {{ ayBasligi }}
            <span class="i-lucide-chevron-down size-3.5 text-n-slate-11" />
          </button>

          <div
            v-if="seciciAcik"
            class="absolute left-1/2 z-30 mt-1 -translate-x-1/2 w-64 p-3 border rounded-xl shadow-lg border-n-weak bg-n-surface-1"
          >
            <div class="flex items-center gap-2 mb-3">
              <Button
                size="sm"
                variant="faded"
                icon="i-lucide-chevron-left"
                :aria-label="t('TAKVIM.ONCEKI_YIL')"
                @click="seciciYil -= 1"
              />
              <!-- Yil ELLE de yazilabilir: 2019'a gitmek icin on kere
                   tiklamak yerine yazmak daha hizli. -->
              <input
                type="number"
                class="!mb-0 text-center grow"
                :value="seciciYil"
                min="1970"
                max="2100"
                :aria-label="t('TAKVIM.YIL')"
                @change="seciciYilYaz"
              />
              <Button
                size="sm"
                variant="faded"
                icon="i-lucide-chevron-right"
                :aria-label="t('TAKVIM.SONRAKI_YIL')"
                @click="seciciYil += 1"
              />
            </div>

            <div class="grid grid-cols-3 gap-1">
              <button
                v-for="(ad, i) in kisaAylar"
                :key="ad"
                type="button"
                class="px-2 py-1.5 text-xs capitalize rounded-md transition-colors"
                :class="
                  seciciYil === odakYil && i === odakAy
                    ? 'bg-n-brand text-white font-medium'
                    : 'text-n-slate-11 hover:bg-n-alpha-1 hover:text-n-slate-12'
                "
                @click="ayaAtla(seciciYil, i)"
              >
                {{ ad }}
              </button>
            </div>

            <button
              type="button"
              class="w-full mt-3 text-xs underline text-n-slate-11 hover:text-n-slate-12"
              @click="buguneGit"
            >
              {{ t('TAKVIM.BUGUN') }}
            </button>
          </div>
        </div>

        <Button
          size="sm"
          variant="faded"
          icon="i-lucide-chevron-right"
          :aria-label="t('TAKVIM.SONRAKI_AY')"
          @click="ayDegistir(1)"
        />
        <Button
          size="sm"
          variant="faded"
          icon="i-lucide-chevrons-right"
          :aria-label="t('TAKVIM.SONRAKI_YIL')"
          @click="yilDegistir(1)"
        />
        <Button
          size="sm"
          variant="ghost"
          color="slate"
          :label="t('TAKVIM.BUGUN')"
          @click="buguneGit"
        />

        <span class="grow" />

        <!-- Gorunum secici: iki secenek icin acilir liste fazla, yan yana
             iki dugme daha hizli. -->
        <div class="flex gap-1 p-0.5 rounded-lg bg-n-alpha-1">
          <button
            type="button"
            class="px-3 py-1 text-xs rounded-md transition-colors"
            :class="
              gorunum === 'ay'
                ? 'bg-n-surface-1 text-n-slate-12 font-medium'
                : 'text-n-slate-11 hover:text-n-slate-12'
            "
            @click="gorunum = 'ay'"
          >
            {{ t('TAKVIM.GORUNUM_AY') }}
          </button>
          <button
            type="button"
            class="px-3 py-1 text-xs rounded-md transition-colors"
            :class="
              gorunum === 'yil'
                ? 'bg-n-surface-1 text-n-slate-12 font-medium'
                : 'text-n-slate-11 hover:text-n-slate-12'
            "
            @click="gorunum = 'yil'"
          >
            {{ t('TAKVIM.GORUNUM_YIL') }}
          </button>
          <button
            type="button"
            class="px-3 py-1 text-xs rounded-md transition-colors"
            :class="
              gorunum === 'liste'
                ? 'bg-n-surface-1 text-n-slate-12 font-medium'
                : 'text-n-slate-11 hover:text-n-slate-12'
            "
            @click="gorunum = 'liste'"
          >
            {{ t('TAKVIM.GORUNUM_LISTE') }}
          </button>
        </div>
      </div>
    </header>

    <div class="flex-1 overflow-auto">
      <div
        class="grid gap-6 p-6"
        :class="panelAcik ? 'xl:grid-cols-[minmax(0,1fr)_22rem]' : ''"
      >
        <section class="min-w-0">
          <p v-if="hata" class="mb-3 text-sm text-n-ruby-11">{{ hata }}</p>

          <!-- Ilk yuklemede izgara yerine bekleme isareti. Sonraki ay
               gecislerinde izgarayi KALDIRMIYORUZ: ekranin bos yanip
               sonmesi gezinmeyi yavas hissettiriyor. -->
          <div
            v-if="yukleniyor && !kayitlar.length"
            class="flex justify-center p-10"
          >
            <Spinner class="text-n-brand" />
          </div>

          <TakvimAy
            v-else-if="gorunum === 'ay'"
            :yil="odakYil"
            :ay="odakAy"
            :kayitlar="kayitlar"
            :secili="seciliGun"
            @gun-secildi="yeniKayit"
            @kayit-secildi="kaydiAc"
          />

          <!-- Yil gorunumu: on iki kucuk ay. Kayit olan gunler isaretli,
               boylece yogun aylar tek bakista secilebiliyor. -->
          <div
            v-else-if="gorunum === 'yil'"
            class="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
          >
            <div
              v-for="(ad, ay) in kisaAylar"
              :key="ad"
              class="p-3 border rounded-xl border-n-weak"
            >
              <button
                type="button"
                class="w-full mb-2 text-xs font-medium text-left capitalize text-n-slate-12 hover:text-n-brand"
                @click="ayaAtla(odakYil, ay)"
              >
                {{ ad }}
              </button>

              <div class="grid grid-cols-7 gap-px text-[10px] text-center">
                <span
                  v-for="gun in gunBasliklari"
                  :key="`${ad}-${gun}`"
                  class="text-n-slate-11"
                >
                  {{ gun.slice(0, 1) }}
                </span>
                <template v-for="(gun, i) in ayIzgarasi(ay)">
                  <span v-if="gun === null" :key="`b-${ad}-${i}`" />
                  <button
                    v-else
                    :key="`g-${ad}-${gun}`"
                    type="button"
                    class="relative py-0.5 rounded hover:bg-n-alpha-1"
                    :class="
                      bugunMu(ay, gun)
                        ? 'bg-n-brand text-white font-medium'
                        : 'text-n-slate-12'
                    "
                    @click="ayaAtla(odakYil, ay)"
                  >
                    {{ gun }}
                    <span
                      v-if="yilinDoluGunleri.has(yilGunAnahtari(ay, gun))"
                      class="absolute left-1/2 bottom-0 size-1 -translate-x-1/2 rounded-full"
                      :class="bugunMu(ay, gun) ? 'bg-white' : 'bg-n-brand'"
                    />
                  </button>
                </template>
              </div>
            </div>
          </div>

          <div v-else class="flex flex-col gap-5">
            <p
              v-if="!listeGunleri.length"
              class="p-10 text-sm text-center border rounded-lg border-n-weak text-n-slate-11"
            >
              {{ t('TAKVIM.KAYIT_YOK') }}
            </p>

            <div v-for="gun in listeGunleri" :key="gun.anahtar">
              <h2
                class="mb-2 text-xs font-medium capitalize text-n-slate-11"
                :class="gun.bugunMu ? 'text-n-brand' : ''"
              >
                {{ gun.baslik }}
              </h2>
              <div class="flex flex-col gap-1">
                <button
                  v-for="kayit in gun.kayitlar"
                  :key="kayitAnahtari(kayit)"
                  type="button"
                  class="flex items-center w-full gap-3 px-3 py-2 text-sm text-left border rounded-lg border-n-weak hover:bg-n-alpha-1"
                  @click="kaydiAc(kayit)"
                >
                  <span
                    class="w-16 text-xs shrink-0"
                    :class="renkSinifi(kayit)"
                  >
                    {{ saatMetni(kayit) }}
                  </span>
                  <span
                    class="truncate text-n-slate-12"
                    :class="durumSinifi(kayit)"
                  >
                    {{ basligiGoster(kayit) }}
                  </span>
                  <span
                    v-if="kayit.tur === 'gonderi'"
                    class="px-1.5 py-0.5 text-xs rounded shrink-0 bg-n-alpha-1 text-n-slate-11"
                  >
                    {{ t('TAKVIM.ROZET_ICERIK') }}
                  </span>
                  <span class="grow" />
                  <span class="text-xs shrink-0 text-n-slate-11">
                    {{ t(DURUM_ANAHTARLARI[kayit.durum] || 'TAKVIM.DURUM_ACIK') }}
                  </span>
                </button>
              </div>
            </div>
          </div>
        </section>

        <!-- Panel ayni sayfada, sagda. Ortu (modal) yerine yan panel:
             kullanici kaydi yazarken takvimi gormeye devam etsin. -->
        <aside
          v-if="panelAcik"
          class="self-start p-4 border rounded-lg border-n-weak bg-n-surface-1"
        >
          <div class="flex items-start gap-2 mb-3">
            <h2 class="text-sm font-medium text-n-slate-12">
              <template v-if="okunanKayit">
                {{ t('TAKVIM.PANEL_GONDERI') }}
              </template>
              <template v-else-if="duzenlemede">
                {{ t('TAKVIM.PANEL_DUZENLE') }}
              </template>
              <template v-else>{{ t('TAKVIM.PANEL_YENI') }}</template>
            </h2>
            <span class="grow" />
            <Button
              size="xs"
              variant="ghost"
              color="slate"
              icon="i-lucide-x"
              :aria-label="t('TAKVIM.KAPAT')"
              @click="paneliKapat"
            />
          </div>

          <!-- SALT OKUNUR: icerikten turemis gonderi kaydi -->
          <div v-if="okunanKayit" class="flex flex-col gap-3">
            <div>
              <p class="text-sm font-medium text-n-slate-12">
                {{ basligiGoster(okunanKayit) }}
              </p>
              <p class="mt-0.5 text-xs" :class="renkSinifi(okunanKayit)">
                {{ zamanMetni(okunanKayit) }}
              </p>
              <p class="mt-0.5 text-xs text-n-slate-11">
                {{ turMetni(okunanKayit) }} &middot;
                {{
                  t(DURUM_ANAHTARLARI[okunanKayit.durum] || 'TAKVIM.DURUM_ACIK')
                }}
              </p>
            </div>

            <p
              v-if="okunanKayit.aciklama"
              class="text-xs whitespace-pre-line text-n-slate-11"
            >
              {{ okunanKayit.aciklama }}
            </p>

            <p
              class="flex items-start gap-2 p-3 text-xs rounded-lg bg-n-alpha-1 text-n-slate-11"
            >
              <span class="mt-px shrink-0 i-lucide-info size-4" />
              <span>{{ t('TAKVIM.GONDERI_ACIKLAMA') }}</span>
            </p>

            <Button
              v-if="gonderiKimligi"
              size="sm"
              variant="faded"
              icon="i-lucide-external-link"
              :label="t('TAKVIM.GONDERI_BAGLANTI')"
              @click="gonderiyeGit"
            />
            <p v-else class="text-xs text-n-slate-11">
              {{ t('TAKVIM.GONDERI_BAGLANTI_YOK') }}
            </p>
          </div>

          <!-- DUZENLENEBILIR: elle eklenen kayit -->
          <form v-else class="flex flex-col gap-3" @submit.prevent="kaydet">
            <label class="text-sm text-n-slate-11">
              {{ t('TAKVIM.FORM_BASLIK') }}
              <input
                v-model="form.baslik"
                type="text"
                required
                :placeholder="t('TAKVIM.FORM_BASLIK_YER')"
              />
            </label>

            <label class="text-sm text-n-slate-11">
              {{ t('TAKVIM.FORM_ACIKLAMA') }}
              <textarea v-model="form.aciklama" rows="3" />
            </label>

            <!-- Tum gun secenegi saat alanlarini gizler: gun boyu suren bir
                 kayitta saat sormak gereksiz soru. -->
            <label class="flex items-center gap-2 text-sm text-n-slate-11">
              <input v-model="form.tum_gun" type="checkbox" class="!mb-0" />
              {{ t('TAKVIM.FORM_TUM_GUN') }}
            </label>

            <div class="grid gap-3 grid-cols-2">
              <label class="text-sm text-n-slate-11">
                {{ t('TAKVIM.FORM_TARIH') }}
                <input v-model="form.gun" type="date" required />
              </label>
              <label v-if="!form.tum_gun" class="text-sm text-n-slate-11">
                {{ t('TAKVIM.FORM_SAAT') }}
                <input v-model="form.saat" type="time" />
              </label>

              <label class="text-sm text-n-slate-11">
                {{ t('TAKVIM.FORM_BITIS_TARIH') }}
                <input v-model="form.bitis_gun" type="date" :min="form.gun" />
              </label>
              <label v-if="!form.tum_gun" class="text-sm text-n-slate-11">
                {{ t('TAKVIM.FORM_BITIS_SAAT') }}
                <input v-model="form.bitis_saat" type="time" />
              </label>
            </div>

            <label class="text-sm text-n-slate-11">
              {{ t('TAKVIM.FORM_TUR') }}
              <select v-model="form.tur">
                <option v-for="tur in TURLER" :key="tur" :value="tur">
                  {{ t(TUR_ANAHTARLARI[tur]) }}
                </option>
              </select>
            </label>

            <label class="text-sm text-n-slate-11">
              {{ t('TAKVIM.FORM_HATIRLATMA') }}
              <select v-model="form.hatirlatma">
                <option
                  v-for="secenek in HATIRLATMA_SECENEKLERI"
                  :key="secenek.deger"
                  :value="secenek.deger"
                >
                  {{ t(secenek.anahtar) }}
                </option>
              </select>
            </label>

            <label class="text-sm text-n-slate-11">
              {{ t('TAKVIM.FORM_DURUM') }}
              <select v-model="form.durum">
                <option v-for="durum in DURUMLAR" :key="durum" :value="durum">
                  {{ t(DURUM_ANAHTARLARI[durum]) }}
                </option>
              </select>
            </label>

            <p v-if="formHatasi" class="text-sm text-n-ruby-11">
              {{ formHatasi }}
            </p>

            <div class="flex flex-wrap gap-2">
              <Button
                type="submit"
                size="sm"
                variant="solid"
                :label="t('TAKVIM.KAYDET')"
                :is-loading="kaydediliyor"
                :disabled="kaydediliyor || siliniyor"
              />
              <Button
                type="button"
                size="sm"
                variant="faded"
                :label="t('TAKVIM.VAZGEC')"
                :disabled="kaydediliyor || siliniyor"
                @click="paneliKapat"
              />
              <Button
                v-if="duzenlemede"
                type="button"
                size="sm"
                variant="ghost"
                color="ruby"
                icon="i-lucide-trash-2"
                :label="t('TAKVIM.SIL')"
                :is-loading="siliniyor"
                :disabled="kaydediliyor || siliniyor"
                @click="sil"
              />
            </div>
          </form>
        </aside>
      </div>
    </div>
  </div>
</template>
